import { useState, useEffect } from "react";
import LocationSelector from "../common/LocationSelector";
import { getUserDetails } from "../../utils/getUserDetails";
import { ApiService } from "../../api/services.gen";
import { useParams, useNavigate } from "react-router";
import { useAlert } from "../../constants/AlertProvider";
import {
    BriefcaseIcon,
    BuildingIcon,
    DollarSignIcon,
    GraduationCapIcon,
    ClockIcon,
    SendIcon,
} from 'lucide-react'
import RecHomeNavbar from "./RecHomeNavbar";

export default function JobPost() {
    const showAlert = useAlert();
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        keywords: "",
        salary_min: "",
        salary_max: "",
        city: "",
        state: "",
        country: "",
        created_by: "",
        company: "",
        is_remote: false,
        job_type: "",
        degree_requirements: "",
        years_of_experience: null,
        benefits_and_perks: "",
        visa_sponsor: false,
    });
    const [createdById, setCreatedById] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    const [companyName, setCompanyName] = useState(null);
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        let ignore = false;

        if (id) {
            // Fetch job data and prefill form
            ApiService.apiJobprofileRetrieve({ id }).then(job => {
                if (ignore) {
                    return;
                }

                setFormData(prev => ({
                    ...prev,
                    ...job,
                    country: job.country || "",
                    state: job.state || "",
                    city: job.city || "",
                }));
            });
        }

        return () => {
            ignore = true;
        };
    }, [id]);

    useEffect(() => {
        let ignore = false;

        const fetchData = async () => {
            try {
                const userDetails = await getUserDetails();
                const userId = userDetails?.id;
                const companyIdFromUser = userDetails?.company;

                if (ignore) {
                    return;
                }

                if (!userId || !companyIdFromUser) {
                    showAlert("User or company details not found. Please complete your profile first.", "warning");
                    navigate('/orgprofile');
                    return;
                }

                setCreatedById(userId);
                setCompanyId(companyIdFromUser);

                const companyRes = await ApiService.apiCompanyRetrieve({ id: companyIdFromUser });
                if (!ignore) {
                    setCompanyName(companyRes.name);
                }
            } catch (error) {
                if (!ignore) {
                    console.error("Error fetching company data:", error);
                    showAlert("Error fetching your profile data.", "error");
                }
            }
        };

        fetchData();

        return () => {
            ignore = true;
        };
    }, [navigate, showAlert]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleLocationChange = ({ countryName, stateName, cityName }) => {
        setFormData(prev => ({
            ...prev,
            country: countryName || "",
            state: stateName || "",
            city: cityName || "",
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const jobData = {
            ...formData,
            salary_min: Number(formData.salary_min),
            salary_max: Number(formData.salary_max),
            created_by: createdById,
            company: companyId,
        };

        try {
            let jobId = id;
            if (id) {
                // Edit mode: update job
                await ApiService.apiJobprofilePartialUpdate({
                    id,
                    requestBody: jobData,
                });
                showAlert("Job updated successfully!", "success");
            } else {
                // Create mode: create job
                const newJob = await ApiService.apiJobprofileCreate({
                    requestBody: jobData,
                });
                jobId = newJob.id;
                showAlert("Job created successfully!", "success");
            }
            if (jobId) {
                // Navigate to the skill posting page with the job's ID
                navigate(`/skillpost/${jobId}`);
            }
        } catch (error) {
            console.error("Error posting/updating job:", error);
            const data = error?.response?.data;
            const firstFieldError =
                data && typeof data === "object"
                    ? Object.values(data).flat().find((v) => typeof v === "string")
                    : null;
            showAlert(firstFieldError || "Failed to post/update job.", "error");
        }
    };

    const handleCancel = () => {
        if (id) {
            navigate("/myjobs");
        } else {
            setFormData({
                title: "",
                description: "",
                keywords: "",
                salary_min: "",
                salary_max: "",
                city: "",
                state: "",
                country: "",
                created_by: createdById,
                company: companyId,
                is_remote: false,
                job_type: "",
                degree_requirements: "",
                years_of_experience: null,
                benefits_and_perks: "",
                visa_sponsor: false,
            });
        }
    };

    return (
        <>
            <RecHomeNavbar />
            <div className="mt-14 min-h-screen bg-gray-50 dark:bg-gray-900 w-full">
                {/* header */}
                <header className="w-full mx-auto px-4 py-8 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                        <BriefcaseIcon className="mr-2 h-6 w-6 text-blue-600" />
                        {id ? "Update Job": "Post a New Job"}
                    </h1>
                </header>
                {/* main-content */}
                <main className="w-full mx-auto px-4 py-8 sm:px-6 lg:px-8">
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                        <form onSubmit={handleSubmit}>
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                    Basic Information
                                </h2>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Provide the basic details about the job position.
                                </p>
                                <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                    {/* job-title */}
                                    <div className="sm:col-span-3">
                                        <label
                                            htmlFor="title"
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            Job Title*
                                        </label>
                                        <div className="mt-1 relative rounded-md shadow-sm">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <BriefcaseIcon className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <input
                                                type="text"
                                                name="title"
                                                id="title"
                                                value={formData.title}
                                                onChange={handleChange}
                                                required
                                                className="focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                                                placeholder="e.g. Senior Software Engineer"
                                            />
                                        </div>
                                    </div>

                                    {/* comapny-name */}
                                    <div className="sm:col-span-3">
                                        <label
                                            htmlFor="company"
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            Company Name*
                                        </label>
                                        <div className="mt-1 relative rounded-md shadow-sm">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <BuildingIcon className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <input
                                                type="text"
                                                name="company"
                                                id="company"
                                                value={companyName || ""}
                                                disabled
                                                className="focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                                                placeholder="e.g. Acme Inc."
                                            />
                                        </div>
                                    </div>

                                    {/* location */}
                                    <LocationSelector
                                        onChange={handleLocationChange}
                                        defaultValue={{
                                            country: formData.country,
                                            state: formData.state,
                                            city: formData.city,
                                        }}
                                    />

                                    {/* job-type */}
                                    <div className="sm:col-span-2">
                                        <label
                                            htmlFor="job_type"
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            Job Type
                                        </label>
                                        <div className="mt-1 relative rounded-md shadow-sm">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <ClockIcon className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <select
                                                id="job_type"
                                                name="job_type"
                                                value={formData.job_type}
                                                onChange={handleChange}
                                                className="focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                                            >
                                                <option value="">Select job type</option>
                                                <option value="full-time">Full-time</option>
                                                <option value="intern">Internship</option>
                                                <option value="volunteering">Volunteering</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* salary */}
                                    <div className="sm:col-span-2">
                                        <label
                                            htmlFor="salary_min"
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            Salary Range (Min)
                                        </label>
                                        <div className="mt-1 relative rounded-md shadow-sm">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <DollarSignIcon className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <input
                                                type="number"
                                                name="salary_min"
                                                id="salary_min"
                                                value={formData.salary_min}
                                                onChange={handleChange}
                                                min={0}
                                                className="focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                                                placeholder="e.g. 50,000"
                                            />
                                        </div>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label
                                            htmlFor="salary_max"
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            Salary Range (Max)
                                        </label>
                                        <div className="mt-1 relative rounded-md shadow-sm">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <DollarSignIcon className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <input
                                                type="number"
                                                name="salary_max"
                                                id="salary_max"
                                                value={formData.salary_max}
                                                onChange={handleChange}
                                                min={0}
                                                className="focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                                                placeholder="e.g. 80,000"
                                            />
                                        </div>
                                    </div>
                                    {/* <div className="sm:col-span-2">
                                        <label
                                            htmlFor="currency"
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            Currency
                                        </label>
                                        <select
                                            id="currency"
                                            name="currency"
                                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md border"
                                        >
                                            <option>USD</option>
                                            <option>EUR</option>
                                            <option>GBP</option>
                                            <option>CAD</option>
                                            <option>INR</option>
                                        </select>
                                    </div> */}
                                </div>
                            </div>
                            {/* JD */}
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                    Job Description
                                </h2>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Provide detailed information about the role and
                                    responsibilities.
                                </p>
                                <div className="mt-6">
                                    <label
                                        htmlFor="description"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        Description*
                                    </label>
                                    <div className="mt-1">
                                        <textarea
                                            id="description"
                                            name="description"
                                            rows={6}
                                            value={formData.description}
                                            onChange={handleChange}
                                            required
                                            className="shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                                            placeholder="Describe the role, responsibilities, and what a typical day looks like..."
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                    Keywords
                                </h2>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    List some keywords for better matching.
                                </p>
                                <div className="mt-6">
                                    <label
                                        htmlFor="keywords"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        Keywords*
                                    </label>
                                    <div className="mt-1">
                                        <textarea
                                            id="keywords"
                                            name="keywords"
                                            rows={4}
                                            required
                                            value={formData.keywords}
                                            onChange={handleChange}
                                            className="shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                                            placeholder="List the required keywords"
                                        />
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <label
                                        htmlFor="degree_requirements"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        Education*
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <GraduationCapIcon className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            name="degree_requirements"
                                            id="degree_requirements"
                                            value={formData.degree_requirements}
                                            onChange={handleChange}
                                            required
                                            className="focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                                            placeholder="e.g. Bachelor's degree in Computer Science or related field"
                                        />
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <label
                                        htmlFor="years_of_experience"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        Years of experience*
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <BriefcaseIcon className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            name="years_of_experience"
                                            id="years_of_experience"
                                            value={formData.years_of_experience ?? ""}
                                            onChange={handleChange}
                                            className="focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                                            placeholder="e.g. 4"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                    Benefits & Perks
                                </h2>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Highlight the benefits and perks offered with this position.
                                </p>
                                <div className="mt-6">
                                    <label
                                        htmlFor="benefits_and_perks"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        Benefits & Perks*
                                    </label>
                                    <div className="mt-1">
                                        <textarea
                                            id="benefits_and_perks"
                                            name="benefits_and_perks"
                                            rows={4}
                                            value={formData.benefits_and_perks}
                                            onChange={handleChange}
                                            required
                                            className="shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                                            placeholder="List health insurance, retirement plans, vacation policy, remote work options, etc..."
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                    Additional Information
                                </h2>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Add work place and visa sponsorship.
                                </p>
                                <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                    <div className="sm:col-span-3">
                                        <label
                                            htmlFor="is_remote"
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            Is this a remote position?
                                        </label>
                                        <input
                                            id="is_remote"
                                            name="is_remote"
                                            type="checkbox"
                                            checked={formData.is_remote}
                                            onChange={e => setFormData(prev => ({ ...prev, is_remote: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-sm focus:ring-blue-500 focus:ring-1"
                                        />
                                    </div>
                                    <div className="sm:col-span-3">
                                        <label
                                            htmlFor="visa_sponsor"
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            Is Visa Sponsorship available?
                                        </label>
                                        <input
                                            id="visa_sponsor"
                                            name="visa_sponsor"
                                            type="checkbox"
                                            checked={formData.visa_sponsor}
                                            onChange={e => setFormData(prev => ({ ...prev, visa_sponsor: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-sm focus:ring-blue-500 focus:ring-1"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="p-6 flex items-center justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="bg-white dark:bg-gray-800 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    <SendIcon className="mr-2 h-5 w-5" />
                                    {id ? "Next, Update Skills" : "Next, Add Skills"}
                                </button>
                            </div>
                        </form>
                    </div>
                </main>
            </div>
        </>
    );
}
